import React, { ReactNode } from 'react';

import { Box, useTheme, styled } from '@mui/material';
import { useClearRefinements } from 'react-instantsearch';

import CloseIcon from '~/assets/icons/CloseIcon';
import IKImage from '~/components/IKImage/IKImage';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useSERPNavigation from '~/hooks/useSERPNavigation';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { HITS_TYPES } from '~/pages/const';
import { css, CssUtilityType } from '~/styled';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX, useIsWidthUp } from '~/theme';

interface FiltersOverlayProps {
  attributesExcludedFromClear?: string[];
  children: ReactNode;
}

const FiltersOverlay: React.FC<FiltersOverlayProps> = ({
  attributesExcludedFromClear,
  children,
}) => {
  const theme = useTheme();
  const { openFiltersPanel, setOpenFiltersPanel } = useDiscoverContext();
  const { tenant } = useTenantContext();
  const { userInfo } = useUserInfoContext();
  const { t } = useTenantTranslation();
  const isMobile = !useIsWidthUp('xl');
  const serpType = useSERPNavigation();

  const logoUrl = userInfo?.company?.logo_url ? userInfo?.company?.logo_url : tenant?.logo_url;

  const { refine, canRefine } = useClearRefinements({
    excludedAttributes: attributesExcludedFromClear,
  });

  const handleCloseOverlay = () => {
    setOpenFiltersPanel(false);
  };

  return (
    <Overlay
      isCoursePage={serpType === HITS_TYPES.COURSE}
      optionalStyles={openFiltersPanel ? generatedOverlayOpen : undefined}
    >
      <ContentWrapper>
        {isMobile && tenant?.logo_url && (
          <OverlayLogoWrapper>
            <IKImage path={logoUrl || ''} draggable={false} alt={'logo'} />
          </OverlayLogoWrapper>
        )}
        {isMobile && (
          <CloseBtnWrapper>
            <StyledCloseIcon color={theme.palette.common.primary} onClick={handleCloseOverlay} />
          </CloseBtnWrapper>
        )}
        {children}
        {serpType !== HITS_TYPES.COURSE && (
          <ButtonsWrapper>
            <CustomButton
              color="secondary"
              variant="outlined"
              onClick={refine}
              disabled={!canRefine}
            >
              {t('button.clearAll')}
            </CustomButton>
            {isMobile && <ApplyBtn onClick={handleCloseOverlay}>{t('button.apply')}</ApplyBtn>}
          </ButtonsWrapper>
        )}
      </ContentWrapper>
    </Overlay>
  );
};

export default FiltersOverlay;

const Overlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles' && prop !== 'isCoursePage',
})<{ isCoursePage: boolean; optionalStyles?: CssUtilityType }>`
  height: 100%;
  width: 0;
  position: fixed;
  z-index: 1200;
  top: 0;
  right: 0;
  overflow-x: hidden;
  transition: width 0.5s;
  background: ${({ theme }) => theme.palette.common.blockBackground.main};
  padding-top: 80px;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    height: unset;
    position: relative;
    width: auto;
    padding-top: 0;
    z-index: 1;
    padding-right: ${({ isCoursePage }) => !isCoursePage && MIDDLE_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    overflow-x: visible;
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const generatedOverlayOpen = ({ theme }) => css`
  width: 100%;
  padding: 24px;

  ${theme.breakpoints.up('sm')} {
    padding: 30px;
  }
`;
const ContentWrapper = styled(Box)`
  overflow: hidden;
  //overflow: visible to not cut the hover circle around a filter option on checkbox from the left side
  ${({ theme }) => theme.breakpoints.up('xl')} {
    overflow: visible;
  }
`;
const OverlayLogoWrapper = styled(Box)`
  display: flex;
  height: 45px;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 60px;
  }

  & img {
    height: 100%;
  }
`;
const CloseBtnWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
  margin: ${MIDDLE_MARGIN_PX} 0;
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 14px;
  height: 14px;
`;
const ButtonsWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  margin: ${MIDDLE_MARGIN_PX} 0;

  & button:last-child:not(:only-child) {
    margin-left: ${SMALL_MARGIN_PX};
  }
`;
const ApplyBtn = styled(CustomButton)`
  && {
    min-width: 139px;
  }
`;
